import classNames from "classnames";
import { Entry } from "contentful";
import Image from "next/image";
import { ImgHTMLAttributes } from "react";

import Editorials from "~/types/editorials";
import CommonUtils from "~/utils/common-utils";
import Constants from "~/utils/constants";
import { isActiveEntry } from "~/utils/editorial-utils";
import imageUtils from "~/utils/image-utils";

import styles from "./media-asset.module.scss";
import VideoLazyLoad from "./video-lazy-load";

type Props = {
  entry: Entry<Editorials.Asset>;
  poster?: string;
  className?: string;
  fill?: boolean;
  width?: number;
  height?: number;
  videoConfig?: {
    muted?: boolean;
    loop?: boolean;
    autoPlay?: boolean;
    controls?: boolean;
    playsInline?: boolean;
  };
  noLazy?: boolean;
} & Omit<ImgHTMLAttributes<HTMLImageElement>, "placeholder">;

const defaultVideoConfig = Constants.DEFAULT_VIDEO_CONFIG;

// by deafult fill rule for next/image is set to true
export default function MediaAsset({
  entry,
  poster,
  className,
  fill = true,
  width,
  height,
  videoConfig = defaultVideoConfig,
  noLazy,
  ...rest
}: Props) {
  if (!isActiveEntry(entry) || !entry.fields.file) return null;

  let mediaUrl = entry.fields.file.url;
  let posterUrl = poster;

  if (CommonUtils.parseBoolean(process.env.NEXT_PUBLIC_ENABLE_CLOUDFRONT_ASSETS)) {
    mediaUrl = mediaUrl.replace(
      Constants.ASSETS_CDN_IMAGES_SOURCE.CONTENTFUL,
      Constants.ASSETS_CDN_IMAGES_SOURCE.CLOUDFRONT
    );
    mediaUrl = mediaUrl.replace(
      Constants.ASSETS_CDN_VIDEOS_SOURCE.CONTENTFUL,
      Constants.ASSETS_CDN_VIDEOS_SOURCE.CLOUDFRONT
    );

    if (posterUrl) {
      posterUrl = posterUrl.replace(
        Constants.ASSETS_CDN_IMAGES_SOURCE.CONTENTFUL,
        Constants.ASSETS_CDN_IMAGES_SOURCE.CLOUDFRONT
      );

      posterUrl = `${posterUrl}?fm=webp`;
    }
  }

  return entry.fields.file.contentType.split("/")[0] === "image" ? (
    <Image
      priority={!!noLazy}
      src={"https:" + mediaUrl}
      alt={entry.fields.description || entry.fields.title || ""}
      className={className}
      fill={!(width || height) ? fill : undefined}
      width={width}
      height={height}
      unoptimized={mediaUrl.endsWith(".svg")}
      loader={imageUtils.contentfulImageLoader}
      {...rest}
    />
  ) : (
    <VideoLazyLoad
      {...videoConfig}
      className={classNames(styles.video, className)}
      poster={videoConfig.autoPlay ? undefined : posterUrl}
      src={"https:" + mediaUrl + (posterUrl || videoConfig.autoPlay ? "" : "#t=0.001")}
      preload={!!noLazy ? "auto" : "metadata"}
    />
  );
}
